// "Everything read" (U18, module 04 Open 29; RecentAndQuestions.dc.html; B181): the raw
// list of what Cobrain has read, behind the ask bar and the records and under no menu entry.
// It was the landing page (B48, the first read path over the corpus, D147): the caller's
// own most recent items, newest first, exactly as the row-level policy hands them over.
// Everything classified they may read, plus their own unclassified mail, and nothing of
// anybody else's — the list makes no filter of its own, so what is on the screen is the rule.
//
// An item the pipeline has not read yet is shown as it stands and labelled as the owner's
// alone. It is not hidden and not held back: owner-first visibility is the point, and a
// user should be able to tell an item Cobrain has read from one it has not.
//
// With the personal toggle on the page asks each store once and merges by time in the app
// (D12, D43); a personal item is drawn in violet, filed by its area (B72).
import { List, ListRow } from './parts/primitives.js';
import { ActionButton } from './parts/ActionButton.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, Unauthenticated, type PersonalArea, type RecentSignal, type Store } from './api.js';
import { useLiveRefresh } from './live.js';
import { formatDate, formatWhen, statusLine } from './labels.js';
import { railsMe, type ReportView } from './rails.js';
import { ViewAsChooser } from './parts/ViewAsBand.js';
// BUGS.md X40 (Mike by pop-up, 9 Oct 2026, "Move it into the new look (Recommended)"): the page is in the Match centre
// look (App.tsx MATCH_CENTRE) and draws its list in /search's grammar, the nearest finished list of what Cobrain read.
import './find/search.css';
import './recent.css';

const KINDS: Record<string, string> = {
  email: 'Email', teams_chat: 'Teams chat', teams_channel: 'Teams channel', cobrain_chat: 'Cobrain',
  whatsapp: 'WhatsApp', call: 'Call', meeting: 'Meeting', calendar_event: 'Calendar', document: 'Document',
  note: 'Note', news_item: 'News', digest: 'Digest', span_extract: 'Extract', manual_note: 'Note',
};

function When({ iso }: { iso: string }) {
  const when = formatWhen(iso);
  return <time dateTime={iso}>{when.kind === 'relative' ? when.text : formatDate(when.iso)}</time>;
}

type Row = RecentSignal & { private: boolean };

function RowView({ s }: { s: Row }) {
  const status = statusLine(s.classificationStatus, s.domainTier, s.personalArea);
  return (
    <ListRow className={`read-row read-${s.classificationStatus}${s.private ? ' private' : ''}`}>
      <span className="read-head">
        <span className="search-t">{s.subject ?? <span className="read-none">(no subject)</span>}</span>
        <span className="read-when"><When iso={s.occurredAt} /></span>
      </span>
      <span className="search-d">
        {s.private ? <><span className="search-p">Private · only you</span><span aria-hidden="true"> · </span></> : null}
        <span>{KINDS[s.kind] ?? s.kind}</span>
        {s.from ? <><span aria-hidden="true"> · </span><span className="read-from">{s.direction === 'outbound' ? 'to ' : ''}{s.from}</span></> : null}
        <span aria-hidden="true"> · </span>
        <span className={`read-status read-status-${s.classificationStatus}`}>{status}</span>
      </span>
    </ListRow>
  );
}

/**
 * `person` (BUGS.md D78): the list is the items that person is on, read in the one store that holds them, as the reader
 * may read them. Their page's "Everything Cobrain has read about them" opens it; the heading names them.
 */
export function Recent({ stores, person }: { stores: Store[]; person?: string }) {
  const store = stores[0]!;
  const key = stores.map((s) => s.tenantId).join('+');
  const [state, setState] = useState<{ kind: 'loading' } | { kind: 'ready'; signals: Row[]; areas: PersonalArea[]; who: string | null; more: boolean } | { kind: 'error'; message: string }>({ kind: 'loading' });
  // BUGS.md X18 (Mike by pop-up, 8 Oct 2026, "All as recommended (Recommended)": "Everything read gets 'Show 50 older'
  // under the list, and its heading becomes 'The newest items Cobrain has read'. No total is counted."): how many of each
  // store's newest items are asked for; each press asks for fifty more, and the list stays on the screen while it reads.
  const [limit, setLimit] = useState(PAGE);
  const growing = useRef(false);
  const [readingOlder, setReadingOlder] = useState(false);
  // B72: which area the list is filtered to, in a personal store. '' is all of them.
  const [area, setArea] = useState('');
  // R6: whose view this person may take. Empty for almost everyone, so the control only
  // appears for someone who actually has a report.
  const [reports, setReports] = useState<ReportView[]>([]);
  const [viewAs, setViewAs] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    railsMe.reports(store.tenantId)
      .then((r) => { if (!cancelled) setReports(r.reports); })
      .catch(() => { /* no reports is the normal case; a failure here is not worth a page error */ });
    return () => { cancelled = true; };
  }, [store.tenantId]);

  /**
   * One read of both stores. `quiet` is the re-read when he comes back to the page (B1262,
   * live.ts): it leaves the list on the screen while it asks, keeps the area he has chosen,
   * and says nothing if it fails -- the page was working a moment ago.
   */
  const read = useCallback((quiet: boolean, signal?: AbortSignal) => {
    if (!quiet) setState({ kind: 'loading' });
    // The manager view is the commercial store's (R6); the personal store is the owner's alone.
    return Promise.all(stores.map((s) => api.recentSignals(s.tenantId, limit, s.kind === 'commercial' && viewAs !== '' ? viewAs : undefined, person)
      .then((r) => ({ signals: r.signals.map((x) => ({ ...x, private: s.kind === 'personal' })), areas: r.areas, who: r.person?.name ?? null }))))
      .then((rs) => {
        if (signal?.aborted) return;
        const signals = rs.flatMap((r) => r.signals).sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt));
        // A store that gave as many as were asked for may hold older ones; one that gave fewer has none left to give.
        const more = limit < MAX_ASKED && rs.some((r) => r.signals.length >= limit);
        setState({ kind: 'ready', signals, areas: rs.flatMap((r) => r.areas), who: rs.find((r) => r.who !== null)?.who ?? null, more });
        // The area filter is the reader's choice; only a change of store or of whose view this
        // is clears it, never a re-read that happens while they are looking at the page.
        if (!quiet) setArea('');
      })
      .catch((e: unknown) => {
        if (signal?.aborted) return;
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        if (quiet) return;
        setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, viewAs, person, limit]);

  // Another store, another view or another person starts again at the newest fifty.
  useEffect(() => { setLimit(PAGE); }, [key, viewAs, person]);
  useEffect(() => {
    const attempt = new AbortController();
    // Fifty older is read quietly: the list stays, the area chosen stays, and the button says it is reading.
    const older = growing.current;
    growing.current = false;
    void Promise.resolve(read(older, attempt.signal)).finally(() => { if (!attempt.signal.aborted) setReadingOlder(false); });
    return () => attempt.abort();
  }, [read]);
  useLiveRefresh(useCallback((signal: AbortSignal) => read(true, signal), [read]));

  // BUGS.md D78: narrowed to one person, the heading says whose items these are, in the words of the link that opened it.
  const who = state.kind === 'ready' ? state.who : null;
  const heading = person
    ? <h1>Everything Cobrain has read about {who ?? 'them'}</h1>
    : <h1>The newest items Cobrain has read</h1>;
  const looking = reports.find((r) => r.principalId === viewAs) ?? null;
  const chooser = <ViewAsChooser reports={reports} viewAs={viewAs} onChange={setViewAs} />;

  if (state.kind === 'loading') return <div className="search-page read-page">{heading}{chooser}<p className="note">Reading mail…</p></div>;
  if (state.kind === 'error') return <div className="search-page read-page">{heading}{chooser}<p className="note">Cobrain could not read that just now. {state.message}</p></div>;
  if (state.signals.length === 0) return <div className="search-page read-page">{heading}{chooser}<p className="note">Nothing here yet.</p></div>;

  // B72: a personal store files by the owner's own areas, so the list says which and can be
  // narrowed to one. A commercial store files by tier and gets no such row.
  const counts = new Map<string, number>();
  for (const s of state.signals) if (s.personalArea) counts.set(s.personalArea, (counts.get(s.personalArea) ?? 0) + 1);
  const areaRow = state.areas.length === 0 ? null : (
    <div className="search-tabs">
      <ActionButton reading type="button" className={area === '' ? 'search-tab on' : 'search-tab'} aria-pressed={area === ''} onClick={() => setArea('')}>Everything</ActionButton>
      {state.areas.map((a) => (
        <ActionButton reading key={a.id} type="button" className={area === a.name ? 'search-tab on' : 'search-tab'} aria-pressed={area === a.name} title={a.gist ?? undefined} onClick={() => setArea(a.name)}>
          {a.name}{counts.get(a.name) ? <> <span className="search-n">{counts.get(a.name)}</span></> : null}
        </ActionButton>
      ))}
    </div>
  );
  const shown = area === '' ? state.signals : state.signals.filter((s) => s.personalArea === area);
  const pending = state.signals.filter((s) => s.classificationStatus === 'pending').length;
  const privateCount = state.signals.filter((s) => s.private).length;
  return (
    <div className="search-page read-page">
      {heading}
      {chooser}
      {areaRow}
      <p className="note">
        {looking ? `${looking.displayName.split(' ')[0]}'s` : 'Your'} {state.signals.length} most recent items{pending > 0 && !looking ? `, ${pending} of them not sorted yet and visible to you alone` : ''}
        {privateCount > 0 ? `, ${privateCount} from your private store` : ''}
        {area === '' ? '' : `, ${shown.length} of them in ${area}`}.
      </p>
      {shown.length === 0
        ? <p className="note">Nothing filed under {area} yet. Cobrain files a personal item when it reads it; items read before areas existed carry none.</p>
        : <List>{shown.map((s) => <RowView key={`${s.private ? 'p' : 'c'}-${s.id}`} s={s} />)}</List>}
      {state.more ? (
        <ActionButton reading type="button" className="search-more" disabled={readingOlder} onClick={() => { growing.current = true; setReadingOlder(true); setLimit((n) => n + PAGE); }}>
          {readingOlder ? 'Reading older items…' : 'Show 50 older'}
        </ActionButton>
      ) : null}
    </div>
  );
}

/** Fifty at first and fifty more a press (BUGS.md X18); the API reads at most MAX_ASKED of a store's items (signals.ts MAX_LIMIT). */
const PAGE = 50;
const MAX_ASKED = 5000;
